import { useState } from 'react'
import { useTranslation } from 'next-i18next'

import { useNearestCities } from '@/lib/locations'
import { toast } from '@/components/toast-notifications'

type ReverseGeocodeResult = {
  city: string
  state: string
  countryCode: string
} | null

type LocationData =
  | (ReverseGeocodeResult & {
      lat: number
      lon: number
      slug: string
    })
  | null

export function useCurrentLocation() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [getNearestCities, { loading: nearestCitiesLoading }] =
    useNearestCities()

  const reverseGeocode = async (coords: GeolocationCoordinates) => {
    const { data } = await getNearestCities({
      variables: {
        input: {
          lat: coords.latitude,
          lon: coords.longitude,
          size: 1
        }
      }
    })
    const location = data?.nearestCities?.[0]
    return location || null
  }

  const getCurrentLocation = () => {
    return new Promise<LocationData>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'))
        return
      }
      setLoading(true)
      return navigator.geolocation.getCurrentPosition(
        async position => {
          return reverseGeocode(position.coords).then(location => {
            setLoading(false)
            resolve(
              location
                ? {
                    ...location,
                    lat: position.coords.latitude,
                    lon: position.coords.longitude
                  }
                : null
            )
          })
        },
        e => {
          const errorMessage = `Geolocation error (${e.code}): ${e.message}`
          console.error('navigator error', errorMessage, e)
          toast(
            t(
              'b.listings.view.location_unavailable.text',
              'We are unable to determine your location. Please check your browser settings and ensure that you have given permission for this website to access your location.'
            ),
            {
              type: 'error',
              title: t(
                'b.listings.view.location_unavailable.title',
                'Search Failed'
              )
            }
          )
          setLoading(false)
          reject(errorMessage)
        }
      )
    })
  }

  return { getCurrentLocation, loading: loading || nearestCitiesLoading }
}
